import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ArticleCard from "@/components/base/ArticleCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import { articles, getArticleById } from "@/mocks/articles";
import { usePageMeta } from "@/hooks/usePageMeta";
import ArticleTimeline from "@/pages/article/components/ArticleTimeline";

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? Math.min(100, (window.scrollY / total) * 100) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const related = useMemo(() => {
    if (!article) return [];
    const sameCategory = articles.filter(
      (item) => item.id !== article.id && item.categoryId === article.categoryId,
    );
    const others = articles.filter(
      (item) => item.id !== article.id && item.categoryId !== article.categoryId,
    );
    return [...sameCategory, ...others].slice(0, 3);
  }, [article]);

  const jsonLd = useMemo(() => {
    if (!article) return undefined;
    return [
      {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: article.title,
        description: article.summary,
        datePublished: article.date,
        inLanguage: "zh-CN",
        articleSection: article.categoryName,
        keywords: article.tags.join(","),
        image: article.cover,
        author: { "@type": "Organization", name: "深夜黑料 · 事件复盘档案库" },
        publisher: { "@type": "Organization", name: "深夜黑料 · 事件复盘档案库" },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://midnight-archive.example/" },
          {
            "@type": "ListItem",
            position: 2,
            name: "复盘档案库",
            item: "https://midnight-archive.example/archive",
          },
          { "@type": "ListItem", position: 3, name: article.title },
        ],
      },
    ];
  }, [article]);

  usePageMeta({
    title: article ? `${article.title} | 深夜黑料 复盘档案` : "档案未找到 | 深夜黑料",
    description: article
      ? article.summary
      : "该复盘档案暂未找到，返回档案库查看其它事件时间线。",
    keywords: article ? article.tags.join(",") : "深夜黑料,复盘档案",
    path: article ? `/article/${article.id}` : "/archive",
    jsonLd,
  });

  if (!article) {
    return (
      <div className="mx-auto flex max-w-content flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200/70 text-foreground-500">
          <i className="ri-file-unknow-line text-3xl" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">档案未找到</h1>
        <p className="mt-3 max-w-md text-sm text-foreground-600">
          这个链接可能已经失效，或对应的复盘尚未归档。回到档案库看看其它事件的完整时间线。
        </p>
        <Link
          to="/archive"
          className="mt-6 whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm font-medium text-foreground-950 transition-colors duration-200 hover:bg-primary-500 cursor-pointer"
        >
          返回复盘档案库
        </Link>
      </div>
    );
  }

  const toc = [
    { id: "overview", label: "事件概览" },
    { id: "timeline", label: "完整时间线" },
    ...article.sections.map((section, index) => ({ id: `section-${index}`, label: section.heading })),
    { id: "takeaways", label: "复盘要点" },
  ];

  return (
    <div className="relative">
      <div className="sticky top-[72px] z-30 h-0.5 w-full bg-transparent">
        <div className="h-full bg-accent-500 transition-[width] duration-150" style={{ width: `${progress}%` }} />
      </div>

      <article className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
        <Breadcrumb
          items={[
            { label: "复盘档案库", to: "/archive" },
            { label: article.categoryName, to: `/archive?category=${article.categoryId}` },
            { label: article.title },
          ]}
        />

        <header className="mt-6 max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-950 px-3.5 py-1 text-xs text-accent-400">
            <i className="ri-folder-open-line" />
            {article.categoryName}
          </span>
          <h1 className="mt-4 font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-[40px] md:leading-tight">
            {article.title}
          </h1>
          <p className="mt-4 font-heading text-base text-foreground-700 md:text-lg">
            {article.subtitle}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200/70 py-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-sm text-accent-600" />
              归档于 {article.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-sm text-accent-600" />
              阅读约 {article.readTime}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-fire-line text-sm text-primary-400" />
              {article.heat.toLocaleString("zh-CN")} 次阅读
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-node-tree text-sm text-secondary-500" />
              {article.timeline.length} 个关键节点
            </span>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {article.tags.map((tag) => (
              <Link
                key={tag}
                to={`/archive?q=${encodeURIComponent(tag)}`}
                className="whitespace-nowrap rounded-full border border-background-300/70 px-3 py-1 text-xs text-foreground-600 transition-colors duration-200 hover:border-accent-600 hover:text-accent-400 cursor-pointer"
              >
                #{tag}
              </Link>
            ))}
          </div>
        </header>

        <figure className="mt-8 overflow-hidden rounded-lg border border-background-200/70">
          <img
            src={article.cover}
            alt={`${article.title} - 事件复盘配图`}
            title={`${article.title} 深夜黑料复盘`}
            className="h-56 w-full object-cover object-top md:h-[420px]"
          />
        </figure>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <section id="overview" className="scroll-mt-28">
              <h2 className="flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                <i className="ri-information-line text-accent-500" />
                事件概览
              </h2>
              <p className="mt-4 rounded-lg border-l-2 border-accent-500 bg-background-100 p-5 text-sm leading-relaxed text-foreground-700 md:text-[15px]">
                {article.summary}
              </p>
            </section>

            <div className="mt-12">
              <ArticleTimeline events={article.timeline} />
            </div>

            <div className="mt-12 space-y-10">
              <h2 className="flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                <i className="ri-file-list-3-line text-accent-500" />
                来龙去脉
              </h2>
              {article.sections.map((section, index) => (
                <section key={section.heading} id={`section-${index}`} className="scroll-mt-28">
                  <h3 className="font-heading text-lg font-semibold text-foreground-950">
                    {String(index + 1).padStart(2, "0")}　{section.heading}
                  </h3>
                  <div className="mt-4 space-y-4">
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph.slice(0, 16)} className="text-sm leading-[1.9] text-foreground-700 md:text-[15px]">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            <section id="takeaways" className="mt-12 scroll-mt-28">
              <h2 className="flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                <i className="ri-lightbulb-flash-line text-accent-500" />
                复盘要点
              </h2>
              <ul className="mt-5 space-y-3">
                {article.takeaways.map((point) => (
                  <li
                    key={point}
                    className="flex items-start gap-3 rounded-lg border border-background-200/70 bg-background-100 p-4"
                  >
                    <i className="ri-checkbox-circle-line mt-0.5 text-lg text-secondary-500" />
                    <span className="text-sm leading-relaxed text-foreground-700">{point}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 rounded-lg border border-dashed border-background-300/70 bg-background-100/60 p-4 text-xs leading-relaxed text-foreground-500">
                修订说明：本文基于公开报道与官方公告整理，未对未公开的私人信息进行推测。若你发现时间线存在误差，欢迎通过联系页面提交可查证的更正线索，我们会在核对后更新本文并记录修订。
              </p>
            </section>
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <nav className="rounded-lg border border-background-200/70 bg-background-100 p-5" aria-label="文章目录">
                <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
                  <i className="ri-list-check-2 text-base text-accent-500" />
                  本文目录
                </h3>
                <ul className="mt-4 space-y-2.5">
                  {toc.map((item) => (
                    <li key={item.id}>
                      <a
                        href={`#${item.id}`}
                        className="flex items-start gap-2 text-sm text-foreground-600 transition-colors duration-200 hover:text-accent-400 cursor-pointer"
                      >
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-accent-600" />
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="mt-6 rounded-lg border border-background-200/70 bg-background-100 p-5">
                <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
                  <i className="ri-bookmark-3-line text-base text-accent-500" />
                  相关复盘
                </h3>
                <div className="mt-2">
                  {related.map((item, index) => (
                    <ArticleCard key={item.id} article={item} variant="compact" index={index} />
                  ))}
                </div>
              </div>

              <div className="mt-6 rounded-lg border border-accent-900/50 bg-gradient-to-b from-primary-950 to-background-100 p-5">
                <p className="flex items-center gap-2 text-xs tracking-[0.2em] text-accent-400">
                  <i className="ri-pen-nib-line text-base" />
                  提交更正线索
                </p>
                <p className="mt-3 text-xs leading-relaxed text-foreground-600">
                  手上有更完整的公开来源？把它发给我们，时间线会因为你的补充而更准确。
                </p>
                <Link
                  to="/contact"
                  className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-xs font-medium text-background-950 transition-colors duration-200 hover:bg-accent-400 cursor-pointer"
                >
                  前往联系页面
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-16 border-t border-background-200/70 pt-10">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            继续考古
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((item) => (
              <ArticleCard key={`foot-${item.id}`} article={item} />
            ))}
          </div>
        </section>
      </article>
    </div>
  );
}